import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, RefreshCw, Sparkles, ImageDown } from 'lucide-react';
import { getAuthHeader } from '../../services/tokenService';
import CarteVivanteMap from './CarteVivanteMap';
import Legende from './Legende';
import DommagesSansTerritoire from './DommagesSansTerritoire';
import TypesDispositifs, { filtrerParType } from './TypesDispositifs';
import FicheTerritoire from './FicheTerritoire';
import { telechargerImage } from './exportImage';
import { COUCHES, ZONES, VUE_ENSEMBLE, couleurType } from './couches';

export default function CarteVivanteAdmin({ superAdmin = false }) {
  const [data, setData] = useState(null);
  const [actives, setActives] = useState(COUCHES.map((c) => c.id));
  const [cible, setCible] = useState(null);
  const [choisi, setChoisi] = useState(null);
  const [export_, setExport] = useState(false);
  const [typeChoisi, setTypeChoisi] = useState(null);
  const refCarte = useRef(null);
  const exporter = async () => {
    setExport(true);
    try { await telechargerImage(refCarte.current, `carte-vivante-${(cible?.nom || 'ensemble').toLowerCase().replace(/[^a-z0-9]+/g, '-')}`); } catch { toast.error("Export de l'image impossible"); }
    setExport(false);
  };
  const charger = useCallback(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/carte-vivante`, { headers: getAuthHeader() }).then((r) => setData(r.data)).catch(() => setData({ territoires: [], totaux: {} }));
  }, []);
  useEffect(() => { charger(); }, [charger]);
  if (!data) return <div className="flex items-center gap-2 p-8 text-slate-500"><Loader2 className="h-5 w-5 animate-spin" />Chargement de la carte…</div>;
  const sansLieu = data.territoires.filter((t) => !t.coords);
  const territoires = filtrerParType(data.territoires, typeChoisi);
  return (
    <section className="space-y-5 rounded-3xl bg-slate-950 p-6 text-white shadow-xl" data-testid="carte-vivante-admin">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.25em] text-emerald-300"><Sparkles className="h-4 w-4" />Carte vivante</p>
          <h2 className="mt-1 text-2xl font-bold sm:text-3xl">Dispositifs, chantiers et dommages</h2>
          <p className="text-sm text-slate-400">Contrats en cours de validité · travaux RCDS nominative en cours · dommages déclarés</p>
        </div>
        <button type="button" onClick={charger} className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-4 py-1.5 text-sm hover:bg-white/10" data-testid="carte-vivante-actualiser"><RefreshCw className="h-4 w-4" />Actualiser</button>
      </div>
      <Legende totaux={data.totaux} actives={actives} setActives={setActives} />
      {actives.includes('dispositifs') && <TypesDispositifs types={data.types} selection={typeChoisi} onSelection={setTypeChoisi} />}
      <div className="flex flex-wrap items-center gap-2">
        {[VUE_ENSEMBLE, ...ZONES].map((z) => (
          <button key={z.nom} type="button" onClick={() => setCible(z.bbox ? { ...z } : null)} data-testid={`carte-zone-${z.nom}`}
            className="rounded-full border border-white/15 px-3 py-1 text-xs transition-colors hover:bg-white/10">{z.nom}</button>
        ))}
        <button type="button" disabled={export_} onClick={exporter} className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-emerald-500 px-4 py-1.5 text-xs font-semibold text-slate-950 disabled:opacity-50" data-testid="carte-telecharger-image">
          <ImageDown className="h-4 w-4" />{export_ ? 'Préparation…' : 'Télécharger en image'}
        </button>
      </div>
      <CarteVivanteMap territoires={territoires} actives={actives} cible={cible} onChoisir={setChoisi} refCarte={refCarte} />
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4" data-testid="carte-vivante-territoires">
        {territoires.map((t) => (
          <button type="button" key={t.territoire} onClick={() => setChoisi(t)} className="rounded-xl border border-white/10 bg-white/5 p-3 text-left text-sm transition-colors hover:bg-white/10" data-testid={`carte-fiche-${t.territoire}`}>
            <p className="font-semibold">{t.territoire}</p>
            <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-300">
              {COUCHES.filter((c) => actives.includes(c.id)).map((c) => <span key={c.id}><b style={{ color: c.couleur }}>{t[c.id]}</b> {c.court.toLowerCase()}</span>)}
            </div>
            {Object.keys(t.types || {}).length > 0 && (
              <div className="mt-2 flex h-1.5 overflow-hidden rounded-full">
                {Object.entries(t.types).map(([k, v]) => <span key={k} title={`${k} : ${v}`} style={{ flex: v, background: couleurType(k) }} />)}
              </div>
            )}
          </button>
        ))}
      </div>
      <FicheTerritoire territoire={choisi && territoires.find((t) => t.territoire === choisi.territoire)} type={typeChoisi} onFermer={() => setChoisi(null)} />
      {sansLieu.length > 0 && <p className="text-xs text-amber-300" data-testid="carte-sans-territoire">Sans territoire identifiable (non placés sur la carte) : {sansLieu.map((t) => t.territoire).join(', ')}.</p>}
      <DommagesSansTerritoire onChange={charger} />
    </section>
  );
}
